import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Cloud, CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      if (!sessionId) {
        setState("error");
        return;
      }
      if (attempts >= MAX_POLLS) {
        setState("pending");
        return;
      }
      attempts += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (res.data.status === "expired" || res.data.payment_status === "failed") {
          setState("error");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const icon = {
    checking: <Loader2 className="h-10 w-10 animate-spin text-primary" />,
    paid: <CheckCircle2 className="h-10 w-10 text-primary" />,
    pending: <Loader2 className="h-10 w-10 text-amber-500" />,
    error: <XCircle className="h-10 w-10 text-destructive" />,
  }[state];

  const copy = {
    checking: { t: "Confirming your payment…", d: "Hang tight while we verify with Stripe." },
    paid: { t: "You're on the Team plan!", d: "Payment confirmed. Your workspace is unlocked." },
    pending: { t: "Payment processing", d: "This is taking a moment. You'll get access as soon as it clears." },
    error: { t: "Something went wrong", d: "We couldn't confirm this payment. No charge was finalized." },
  }[state];

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-5">
      <Link to="/" className="absolute left-6 top-6 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <Cloud className="h-5 w-5" strokeWidth={2.2} />
        </span>
        <span className="font-display text-xl font-bold">Nimbus</span>
      </Link>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-accent">{icon}</div>
        <h1 className="mt-5 font-display text-2xl font-bold text-foreground">{copy.t}</h1>
        <p className="mt-2 text-muted-foreground">{copy.d}</p>
        <Button asChild className="mt-7 w-full rounded-full bg-primary font-semibold">
          <Link to="/workspace">Go to workspace <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
        </Button>
      </div>
    </div>
  );
};

export default PaymentSuccess;
